import { Injectable } from '@angular/core';
import moment from "moment";

/** Preset keys shared with admin report filters plus timesheet-only options. */
export type TimesheetDatePreset =
  | 'today'
  | 'last7days'
  | 'last14daysExcludeToday'
  | 'last30days'
  | 'thisMonth'
  | 'previousMonth'
  | 'last90days';

@Injectable({ providedIn: 'root' })
export class TimesheetPresetDateRangeService {
  /**
   * Computes [start, end] for a preset, anchored to `maxDate` (typically server "today")
   * and clamped to [minDate, maxDate].
   */
  getPresetRange(
    preset: TimesheetDatePreset,
    maxDate: Date,
    minDate: Date
  ): { start: Date; end: Date } {
    const today = moment(maxDate).startOf('day');
    const min = moment(minDate).startOf('day');
    const maxEnd = moment(maxDate).endOf('day');
    let start: moment.Moment;
    let end: moment.Moment;

    switch (preset) {
      case 'today':
        start = today.clone();
        end = today.clone().endOf('day');
        break;
      case 'last7days':
        start = today.clone().subtract(7, 'days').startOf('day');
        end = today.clone().subtract(1, 'days').endOf('day');
        break;
      /** Last 14 full calendar days before today (ends yesterday); pairs with compare “previous period” as prior 14 days. */
      case 'last14daysExcludeToday':
        // Mirror last7days: N calendar days ending yesterday (today excluded).
        end = today.clone().subtract(1, 'days').endOf('day');
        start = today.clone().subtract(14, 'days').startOf('day');
        break;
      case 'last30days':
        start = today.clone().subtract(30, 'days').startOf('day');
        end = today.clone().endOf('day');
        break;
      case 'thisMonth':
        start = today.clone().startOf('month');
        end = today.clone().endOf('day');
        break;
      case 'previousMonth':
        start = today.clone().subtract(1, 'month').startOf('month');
        end = today.clone().subtract(1, 'month').endOf('month');
        break;
      case 'last90days':
        start = today.clone().subtract(90, 'days').startOf('day');
        end = today.clone().endOf('day');
        break;
      default:
        start = today.clone();
        end = maxEnd.clone();
    }

    if (start.isBefore(min)) {
      start = min.clone();
    }
    if (end.isAfter(maxEnd)) {
      end = maxEnd.clone();
    }
    if (end.isBefore(start)) {
      end = start.clone();
    }

    return { start: start.toDate(), end: end.toDate() };
  }
}
